import React from 'react';
import { useParams } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import { ArrowLeft } from 'lucide-react';

import styled from 'styled-components';

import { PAGES } from 'constants/pages';

import { useGetDetailedCategoryQuery, useUpdateCategoryMutation, TotadminPermission } from 'data/__generated__';

import { GeneralInfoSection } from 'pages/Categories/CategoryDetails/GeneralInfoSection';
import { StatesSection } from 'pages/Categories/CategoryDetails/StatesSection';

import { Loader } from 'components/Loader';
import { Header, HeaderTitle } from 'components/Header';
import { ActionsAndSave, type PageActionGroup } from 'components/PageHeader';
import { PageTitle } from 'components/PageTitle';
import { SubcategoriesSection } from './SubcategoriesSection';

import {
    CategoryFormData,
    CategoryFormSchema,
    defaultCategoryValues,
} from 'pages/Categories/CategoryDetails/category.schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { AuditLogsSection } from 'components/AuditLogs/AuditLogsSection';

export const CategoryDetails = () => {
    const { categoryId = '' }: { categoryId?: string } = useParams();

    const methods = useForm<CategoryFormData>({
        resolver: zodResolver(CategoryFormSchema),
        defaultValues: defaultCategoryValues,
    });

    const {
        formState: { isDirty },
        handleSubmit,
        reset,
    } = methods;

    const {
        data: categoryData,
        loading: categoryLoading,
        error: categoryError,
    } = useGetDetailedCategoryQuery({
        variables: {
            categoryId,
        },
    });

    const [updateCategory, { loading: updateLoading }] = useUpdateCategoryMutation();

    // Mettre à jour les valeurs par défaut quand les données sont chargées
    React.useEffect(() => {
        if (categoryData?.detailedCategory) {
            reset(categoryData.detailedCategory);
        }
    }, [categoryData?.detailedCategory, reset]);

    if (categoryLoading) {
        return (
            <Container>
                <Loader />
            </Container>
        );
    }

    if (categoryError || !categoryData) {
        throw new Error('Une erreur est survenue lors de la récupération de la catégorie');
    }

    const { detailedCategory } = categoryData;

    if (!detailedCategory) {
        throw new Error(`Aucun produit avec l'id ${categoryId} n'a été trouvé`);
    }

    const handleFormSubmit = handleSubmit(async (fields) => {
        const { data } = await updateCategory({
            variables: {
                categoryId,
                fields,
            },
        });

        if (data) {
            const {
                updateCategory: { success, error },
            } = data;
            if (success) {
                toast.success(`La catégorie "${fields.title}" a bien été modifiée`);
                reset(fields);
            } else {
                if (error) {
                    toast.error(error);
                } else {
                    throw Error("Une erreur inconnue s'est produite");
                }
            }
        } else {
            throw Error("Une erreur inconnue s'est produite");
        }
    });

    const actionGroups: PageActionGroup[] = [
        {
            label: 'Navigation',
            items: [{ label: 'Retour à la liste', icon: <ArrowLeft />, to: '/categories' }],
        },
    ];

    return (
        <Container>
            <FormProvider {...methods}>
                <Form onSubmit={handleFormSubmit}>
                    <Header>
                        <HeaderTitle>
                            <PageTitle page={PAGES.categoryDetails} />
                        </HeaderTitle>
                        <ActionsAndSave
                            actionGroups={actionGroups}
                            onSave={handleFormSubmit}
                            saveDisabled={!isDirty}
                            saveLoading={updateLoading}
                            saveTitle="Mettre à jour"
                            saveRequiredPermissions={[TotadminPermission.CategoriesManage]}
                        />
                    </Header>
                    <Content>
                        <ScrollableContent>
                            <GeneralInfoSection category={detailedCategory} />
                            <StatesSection category={detailedCategory} isDirty={isDirty} />
                            <SubcategoriesSection categoryId={categoryId} />
                            <AuditLogsSection collectionDocumentIds={[categoryId]} />
                        </ScrollableContent>
                    </Content>
                </Form>
            </FormProvider>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Form = styled.form`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const ScrollableContent = styled.div`
    padding: 15px;
    width: 100%;
    height: 100%;
    overflow-y: auto;

    & > :not(:first-child) {
        margin-top: 10px;
    }
`;
